import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, List, ListItem, ListItemText,
  ListItemIcon, ListItemButton, Chip, CircularProgress, Divider,
} from '@mui/material';
import {
  Notifications as NotifIcon,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  DoneAll,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import notificationsApi, { Notification } from '../services/notificationService';

const typeIconMap: Record<string, JSX.Element> = {
  EVALUATION_SUBMITTED: <Warning color="warning" />,
  EVALUATION_APPROVED: <CheckCircle color="success" />,
  EVALUATION_REJECTED: <ErrorIcon color="error" />,
};

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    notificationsApi.list().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleClick = async (n: Notification) => {
    if (!n.is_read) {
      await notificationsApi.markRead(n.id);
      setItems(prev => prev.map(i => i.id === n.id ? { ...i, is_read: true } : i));
    }
    if (n.evaluation_id) {
      const path = n.type === 'EVALUATION_SUBMITTED'
        ? `/evaluations/${n.evaluation_id}/approve`
        : `/evaluations/${n.evaluation_id}`;
      navigate(path);
    }
  };

  const handleMarkAllRead = async () => {
    await notificationsApi.markAllRead();
    setItems(prev => prev.map(i => ({ ...i, is_read: true })));
  };

  const fmtDate = (d?: string | null) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{t('notifications.title', 'Notifications')}</Typography>
        <Button size="small" startIcon={<DoneAll />} onClick={handleMarkAllRead}>
          {t('notifications.markAllRead', 'Mark all read')}
        </Button>
      </Box>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('notifications.empty', 'No notifications')}</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {items.map((n, idx) => (
              <React.Fragment key={n.id}>
                {idx > 0 && <Divider />}
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleClick(n)}
                    sx={{
                      py: 2,
                      bgcolor: n.is_read ? 'transparent' : 'action.hover',
                      minHeight: 48,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {typeIconMap[n.type] || <NotifIcon />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={n.is_read ? 400 : 600}>
                            {n.title}
                          </Typography>
                          {!n.is_read && <Chip label="New" size="small" color="primary" sx={{ height: 20 }} />}
                        </Box>
                      }
                      secondary={
                        <Box>
                          {n.message && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{n.message}</Typography>}
                          <Typography variant="caption" color="text.secondary">{fmtDate(n.created_at)}</Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default NotificationsPage;
